import * as React from 'react';
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import { CircularProgress, FormHelperText, MenuItem, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { GetReasonsMenu } from '../other/ConfigBasic';
import { useAddLogbookReason } from '../../api/LogbookApi';
import { trackEvent } from '../../utils';
import theme from '../../themes/theme';

/**
 * Creates a new reason for the current logbook. It is controlled from the outside
 * with open/onClose, so it can be used on the settings page and in the tour edit form.
 *
 * @param {Object} props
 * @param {Boolean} props.open, controls the dialog visibility
 * @param {Function} props.onClose, closes the dialog
 * @param {String} props.initialLabel, optional value that was typed in the tour form
 * @param {Function} props.onAdded, receives the created or existing reason
 * @param {Function} props.setMessage, function to set a message for the snackbar
 */
export default function DialogAddLogbookReason(props) {
    const { t } = useTranslation();
    const currentUserId = useOutletContext().loginUser.id;
    const logbookId = useOutletContext().logbookId;
    const { open, onClose, initialLabel, onAdded, setMessage } = props;
    const [reason, setReason] = useState('');
    const [shared, setShared] = useState(t('all'));
    const [validationErrors, setValidationErrors] = useState({ reason: false });
    const { addReason, loading } = useAddLogbookReason({ logbookId, currentUserId, setMessage });
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        if (open) {
            setReason(initialLabel === undefined ? '' : initialLabel);
            setShared(t('all'));
            setValidationErrors({ reason: false });
        }
    }, [open, initialLabel, t]);

    const handleSave = async () => {
        if (loading) {
            return;
        }

        if (!formValidation()) {
            return;
        }

        const result = await addReason(reason, shared === t('all'));

        if (result.status === 'created' || result.status === 'duplicate') {
            trackEvent('reasons', result.status === 'created' ? 'reason_created' : 'reason_duplicate', 'Reason added');

            if (onAdded !== undefined) {
                onAdded(result.reason);
            }
            if (onClose !== undefined) {
                onClose();
            }
        } else if (result.status === 'error') {
            trackEvent('reasons', 'reason_create_error', 'Reason creation failed');
        }
    };

    const handleCancel = () => {
        if (loading) {
            return;
        }
        setReason('');
        setValidationErrors({ reason: false });
        if (onClose !== undefined) {
            onClose();
        }
    };

    const handleReasonChange = (event) => {
        setReason(event.target.value);
    };

    const handleSharedChange = (event) => {
        setShared(event.target.value);
    };

    const formValidation = () => {
        let validationIsSuccessful = true;
        let validationErrors = {
            reason: false,
        };

        if (reason.trim().length === 0) {
            validationErrors.reason = true;
            validationIsSuccessful = false;
        }

        setValidationErrors(validationErrors);

        return validationIsSuccessful;
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            fullScreen={isMobile}
            PaperProps={{
                style: {
                    borderRadius: '28px',
                },
            }}
        >
            <DialogTitle sx={{ padding: '24px 24px 16px 24px' }}>{t('createReason')}</DialogTitle>
            <DialogContent sx={{ padding: '0px 24px 0px 24px' }}>
                <Box
                    sx={{
                        padding: '5px 0',
                        maxWidth: isMobile ? '100%' : '296px',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '16px' : 0,
                    }}
                >
                    <FormControl error={validationErrors.reason} sx={{ minWidth: isMobile ? '100%' : '152px', paddingRight: isMobile ? 0 : '8px', flexFlow: 1 }}>
                        <TextField
                            autoFocus
                            error={validationErrors.reason}
                            label={t('reason')}
                            variant='outlined'
                            value={reason}
                            onFocus={(event) => event.target.select()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    handleSave();
                                }
                            }}
                            onChange={(event) => handleReasonChange(event)}
                        />
                        {validationErrors.reason && <FormHelperText>{t('mandatoryField')}</FormHelperText>}
                    </FormControl>

                    <FormControl sx={{ width: isMobile ? '100%' : '140px' }}>
                        <TextField select label={t('sharedWith')} variant='outlined' value={shared} onChange={(event) => handleSharedChange(event)}>
                            {GetReasonsMenu().map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ height: isMobile ? 'auto' : '88px', padding: '24px', gap: isMobile ? '12px' : 0 }}>
                <Button
                    variant='text'
                    sx={{ height: '40px', flex: isMobile ? 1 : 'initial', marginBottom: '0px !important' }}
                    color='secondary'
                    disabled={loading}
                    onClick={() => handleCancel()}
                >
                    {t('cancel')}
                </Button>
                <Button
                    variant='contained'
                    sx={{ height: '40px', flex: isMobile ? 1 : 'initial', marginBottom: '0px !important' }}
                    color='success'
                    disabled={loading || reason.trim().length === 0}
                    onClick={() => handleSave()}
                >
                    {loading && <CircularProgress size={16} color='inherit' sx={{ marginRight: '8px' }} />}
                    {t('create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
